import { useState } from "react";
import { api } from "../api.ts";
import { currentMonth, money } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { errorText } from "../meta.ts";
import { hapticTap } from "../telegram.ts";
import { invalidateRemote, useRemote } from "../useRemote.ts";
import type { AccountBalanceDto } from "@home/shared";
import { AccountSheet } from "./AccountSheet.tsx";
import type { Currency } from "./AmountField.tsx";
import { Segmented } from "./Segmented.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

/** Строки счетов: нажатие открывает счёт (остаток подробно, «Сверить остаток», операции). */
export function AccountRows({ accounts, lang, onPick }: { accounts: AccountBalanceDto[]; lang: Lang; onPick: (id: string) => void }) {
  return (
    <ul className="row-list">
      {accounts.map((a) => (
        <li key={a.id}>
          <button type="button" className="row row-btn" onClick={() => onPick(a.id)}>
            <span className="row-icon" aria-hidden="true">
              {a.stash ? "🐷" : a.currency === "USD" ? "💵" : "💳"}
            </span>
            <div className="row-body">
              <div className="row-title">
                {a.name}
                {a.isDefault && <span className="badge">{t("defaultBadge")}</span>}
                {a.personal && !a.stash && <span className="badge">🔒 {t("personalAcc")}</span>}
              </div>
              {a.currency !== "UZS" && <div className="row-meta">{a.balanceUzs === null ? t("noRateShort") : `≈ ${money(a.balanceUzs, "UZS", lang)}`}</div>}
            </div>
            <div className={a.balance < 0 ? "row-end amount negative" : "row-end amount"}>{money(a.balance, a.currency, lang)}</div>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Счета и карты с остатками; добавление счёта (общий, личный или заначка, сум или доллары). */
export function AccountsSheet({ lang, onClose, startAdding = false }: { lang: Lang; onClose: () => void; startAdding?: boolean }) {
  const month = currentMonth();
  // Тот же ключ, что у обзора: остатки берём из его данных.
  const remote = useRemote(`dashboard:${month}`, () => api.dashboard(month));
  const [adding, setAdding] = useState(startAdding);
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState<Currency>("UZS");
  const [personal, setPersonal] = useState(false);
  const [stash, setStash] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const accounts = remote.data?.accounts ?? [];
  // Заначка — отдельно: в общий остаток она не входит.
  const counted = accounts.filter((a) => !a.stash);
  const stashes = accounts.filter((a) => a.stash);

  const submit = async () => {
    if (busy) return;
    if (!adding) {
      setAdding(true);
      return;
    }
    if (!name.trim()) return setError(t("fillName"));
    setBusy(true);
    setError(null);
    try {
      await api.createAccount({ name: name.trim(), currency, personal: personal || stash, stash });
      hapticTap();
      toast(t("savedOk"));
      invalidateRemote();
      setAdding(false);
      setName("");
      setPersonal(false);
      setStash(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={`💳 ${t("accountsTitle")}`} onClose={onClose}>
      {!remote.data && remote.loading ? (
        <div className="skeleton skeleton-block" />
      ) : (
        <>
          <AccountRows accounts={counted} lang={lang} onPick={setOpened} />
          {stashes.length > 0 && (
            <>
              <h3 className="section-title">{t("stashSection")}</h3>
              <AccountRows accounts={stashes} lang={lang} onPick={setOpened} />
            </>
          )}
        </>
      )}

      {adding && (
        <div className="form add-form">
          <h3 className="form-title">{t("addAccount")}</h3>
          <label className="field">
            <span>{t("accountName")}</span>
            <input className="input" autoFocus placeholder={t("accountNamePh")} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
          </label>
          <div className="field">
            <span>{t("currency")}</span>
            <Segmented
              value={currency}
              onChange={setCurrency}
              label={t("currency")}
              options={[
                { value: "UZS", label: lang === "uz" ? "So'm" : "Сумы" },
                { value: "USD", label: "$ USD" },
              ]}
            />
          </div>
          <label className="toggle">
            <input type="checkbox" checked={personal || stash} disabled={stash} onChange={(e) => setPersonal(e.target.checked)} />
            <span>{t("personalAccount")}</span>
          </label>
          <label className="toggle">
            <input type="checkbox" checked={stash} onChange={(e) => setStash(e.target.checked)} />
            <span>{t("stashAccount")}</span>
          </label>
        </div>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {/* Нижней кнопкой Telegram управляет последний лист с кнопкой — пока открыт счёт, своей не показываем. */}
      {!opened && <PrimaryAction label={adding ? t("save") : `＋ ${t("addAccount")}`} onClick={() => void submit()} busy={busy} />}
      {opened && <AccountSheet id={opened} lang={lang} onClose={() => setOpened(null)} />}
    </Sheet>
  );
}
